// Per-user capability switchboard. Every heavy faculty Sophia owns can be
// turned on or off here; the setting is local-first and sticky across reloads
// so a device that says "no scraping" stays that way.
import { getOption, setOption, subscribeOptions } from "./persistOptions";

export type CapabilityId =
  | "agentic"
  | "holographic"
  | "plasma"
  | "geode"
  | "scraping"
  | "synth"
  | "offlineRadio"
  | "clancy"
  | "kodac"
  | "meridian"
  | "sable"
  | "mosaic"
  | "ledger"
  | "vigil";

export type CapabilityDef = {
  id: CapabilityId;
  label: string;
  detail: string;
  /** Whether the faculty touches the network at all. */
  network: boolean;
  defaultOn: boolean;
  /** False = not implemented yet; its controls must stay inert. */
  available?: boolean;
};

/** Faculties that are designed but not wired to anything real yet. */
export const PLANNED_CAPABILITIES: Array<{ label: string; detail: string }> = [
  { label: "Drone swarm control", detail: "Direct flight control of paired drones. Needs a hardware bridge and flight-safety interlocks before any button can arm." },
  { label: "Dark-web crawler", detail: "Tor-routed harvesting into the vault. Held back until the onion transport and domain policy pass their audit." },
  { label: "Interdimensional relay uplink", detail: "Automatic message relay over the resonance carrier. Currently observation-only — transmit is disabled." },
  { label: "LoRa mesh dispatch", detail: "Send over the LilyGO T-Beam mesh. Waiting on the serial/BLE bridge for the radio." },
];

export const CAPABILITIES: CapabilityDef[] = [
  { id: "agentic", label: "Agentic intelligence", detail: "Background goal loops: plan, pursue, review, report.", network: false, defaultOn: true },
  { id: "holographic", label: "Holographic memory", detail: "Hypercube-face memory snapshots and multi-angle recall.", network: false, defaultOn: true },
  { id: "plasma", label: "Plasma field", detail: "Associative reach — loose links across distant neurons.", network: false, defaultOn: true },
  { id: "geode", label: "Geode intelligence", detail: "Shell classification of memory by hardness and age.", network: false, defaultOn: true },
  { id: "scraping", label: "Automatic scraping", detail: "Keyword-driven web harvesting. Uses the network.", network: true, defaultOn: false },
  { id: "synth", label: "Synth playback", detail: "Resonance / binaural tone generation through your speakers.", network: false, defaultOn: true },
  { id: "clancy", label: "CLANCY intelligence module", detail: "Clandestine Analysis, Corroboration & Yield — tradecraft mode: bottom line up front, calibrated confidence, graded sourcing, competing hypotheses.", network: false, defaultOn: true },
  { id: "kodac", label: "KODAC imagery module", detail: "Kinetic Optical Detection, Analysis & Capture — imagery intelligence: frame, provenance, light, scale, place, integrity, judgement, with manipulation and AI-generation tells.", network: false, defaultOn: true },
  { id: "meridian", label: "MERIDIAN geospatial module", detail: "Mapping, Elevation, Route & Infrastructure Deduction — place from terrain and sky cues, line of sight, route timings, infrastructure dependencies.", network: false, defaultOn: true },
  { id: "sable", label: "SABLE signals module", detail: "Signals Analysis, Bearing, Localisation & Emissions — emitter characterisation, cross-fixes, jamming and spoofing tells, own emission footprint.", network: false, defaultOn: true },
  { id: "mosaic", label: "MOSAIC corroboration module", detail: "Multi-Origin Source Aggregation & Identity Corroboration — independent-origin tracing, circular-reporting detection, timeline reconciliation. No dossiers on private people.", network: false, defaultOn: true },
  { id: "ledger", label: "LEDGER financial module", detail: "Linked Entity, Disbursement, Governance & Exposure Review — ownership versus control, money flow hop by hop, red-flag patterns, settling documents.", network: false, defaultOn: true },
  { id: "vigil", label: "VIGIL safeguarding module", detail: "Victim-first Intervention, Guardianship, Impact & Liaison — immediate-risk read, ordered next steps, evidence preservation without re-traumatising.", network: false, defaultOn: true },
  { id: "offlineRadio", label: "Offline radio learning", detail: "Builds the airwave map from locally received audio only.", network: false, defaultOn: false },
];

const key = (id: CapabilityId) => `cap:${id}`;

export function isCapabilityAvailable(id: CapabilityId): boolean {
  return CAPABILITIES.find((c) => c.id === id)?.available !== false;
}

export function isCapabilityOn(id: CapabilityId): boolean {
  const def = CAPABILITIES.find((c) => c.id === id);
  if (def?.available === false) return false;
  return getOption<boolean>(key(id), def?.defaultOn ?? false);
}

export function setCapability(id: CapabilityId, on: boolean) {
  if (!isCapabilityAvailable(id)) return; // inert until implemented
  setOption(key(id), on);
}

export function toggleCapability(id: CapabilityId): boolean {
  const next = !isCapabilityOn(id);
  setCapability(id, next);
  return next;
}

export function getCapabilityState(): Record<CapabilityId, boolean> {
  const out = {} as Record<CapabilityId, boolean>;
  for (const c of CAPABILITIES) out[c.id] = c.available === false ? false : isCapabilityOn(c.id);
  return out;
}

export function resetCapabilities() {
  for (const c of CAPABILITIES) setCapability(c.id, c.defaultOn);
}

export function subscribeCapabilities(fn: (state: Record<CapabilityId, boolean>) => void): () => void {
  const unsub = subscribeOptions(() => fn(getCapabilityState()));
  fn(getCapabilityState());
  return unsub;
}
